import React, { useState } from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { Sector } from "recharts";

// Custom label rendering function - Show counts instead of percentages
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  value,
  name,
  percent,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Only show value if it's meaningful (> 0)
  if (value <= 0) return null;

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize="12"
      fontWeight="bold"
    >
      {`${value}`}
    </text>
  );
};

// Custom label for donut charts - always visible
const renderDonutLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  value,
  name,
  percent,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (value <= 0) return null;

  return (
    <g>
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="13"
        fontWeight="bold"
        fill="#000000ff"
      >
        {`${value}`}
      </text>
      <text
        x={x}
        y={y + 13}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="10"
        fill="#000000b6"
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    </g>
  );
};

// Custom Legend Component
const CustomLegend = ({ items, colorMap }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      mt: 2,
      gap: 1,
    }}
  >
    {items.map((item) => (
      <Box key={item} sx={{ display: "flex", alignItems: "center", mr: 2 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            backgroundColor: colorMap[item] || "#6c757d", // fallback color
            borderRadius: "2px",
            mr: 1,
          }}
        />
        <Typography variant="caption" sx={{ fontSize: "11px" }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

// Helper function to get data description
const getDataDescription = (title, xAxis, yAxis) => {
  const descriptionMap = {
    "Total Trips ": "Distribution of trip completion status",
    "Route Deviation": "Route deviation incidents by vehicle",
    Default: `${yAxis} distribution by ${xAxis}`,
  };

  return descriptionMap[title] || descriptionMap["Default"];
};

// Update the getChartColors function
const getChartColors = (chartTitle) => {
  // Special case for Total Trips charts - fix the condition
  if (chartTitle.toLowerCase().includes("trips ")) {
    return {
      "On Time": "#28a745", // Green
      Completed: "#28a745", // Green
      Delayed: "#ffc107", // Yellow
      Aborted: "#dc3545", // Red
      "No Trips": "#6c757d", // Gray
      Unknown: "#6f42c1", // Purple
    };
  }

  // Return colors array for other charts
  const defaultColors = [
    "#28a745",
    "#20A8E0",
    "#FFC94D",
    "#008C23",
    "#FF9FF3",
    "#54A0FF",
    "#5F27CD",
    "#00D2D3",
    "#FF9F43",
  ];
  return defaultColors;
};

// Enhanced PieChart Component
const PieChart = ({
  data = [],
  title = "",
  color = "#8884d8",
  width = 400,
  height = 300,
  xAxis = "status",
  yAxis = "count",
  // New props for data description
  dataLabel = "",
  showDataDescription = true,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Transform data to match expected format
  const transformedData = data.map((item) => ({
    name: item[xAxis] || item.status || item.name || "Unknown",
    value: item[yAxis] || item.count || item.value || 0,
    status: item[xAxis] || item.status || item.name || "Unknown",
    count: item[yAxis] || item.count || item.value || 0,
  }));

  // Get colors based on chart type
  const colors = getChartColors(title);

  // Modified color assignment logic
  const getItemColor = (entry, index) => {
    if (title.toLowerCase().includes("trips ")) {
      // Fix the condition here too
      return colors[entry.name] || colors["Unknown"];
    }
    // For other charts, use the colors array
    return Array.isArray(colors) ? colors[index % colors.length] : colors[0];
  };

  // Create color map for legend
  const legendColorMap = {};
  transformedData.forEach((item, index) => {
    if (title.toLowerCase().includes("trips")) {
      // Fix the condition here as well
      legendColorMap[item.name] = colors[item.name] || colors["Unknown"];
    } else {
      legendColorMap[item.name] = Array.isArray(colors)
        ? colors[index % colors.length]
        : colors[0];
    }
  });

  const onPieEnter = (_, index) => setActiveIndex(index);

  // Check if this is a halt or deviation chart for special styling
  const isSpecialChart =
    title.toLowerCase().includes("halt") ||
    title.toLowerCase().includes("deviation") ||
    title.toLowerCase().includes("route deviation") ||
    title.toLowerCase().includes("Halts");

  // Check if this is a filled pie chart (not donut)
  const isFilledPieChart = !isSpecialChart;

  const shouldShowLabels = true;
  // Get data description
  const dataDescription = dataLabel || getDataDescription(title, xAxis, yAxis);

  // Calculate total for display
  const totalValue = transformedData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "14px",
          color: "#333",
        }}
      >
        {title}
      </Typography>

      {transformedData.length > 0 ? (
        <>
          <ResponsiveContainer width={width} height={height + 10}>
            <RechartsPieChart>
              <Pie
                data={transformedData}
                cx="50%"
                cy="50%"
                innerRadius={isSpecialChart ? 40 : 0}
                outerRadius={90}
                dataKey="value"
                nameKey="name"
                labelLine={false} // Changed from {isSpecialChart} to {false}
                label={
                  isSpecialChart ? renderDonutLabel : renderCustomizedLabel
                }
              >
                {transformedData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.name}`}
                    fill={getItemColor(entry, index)}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => {
                  const label = name === "deviations" ? "Deviations" : name;
                  return [value, label];
                }}
                labelStyle={{ color: "#333" }}
                contentStyle={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>

          {/* Show legend for filled pie charts or special charts */}
          {(isFilledPieChart || isSpecialChart) && (
            <CustomLegend
              items={transformedData.map((item) => item.name)}
              colorMap={legendColorMap}
            />
          )}
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: height - 80,
            color: "#666",
          }}
        >
          <Typography>No data available</Typography>
        </Box>
      )}
    </Box>
  );
};

export default PieChart;
